import React, { useState, useEffect, useMemo } from "react";
import RapportX from "../../Services/RapportX";
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  TextField,
  Box,
  useMediaQuery,
  TablePagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  alpha
} from "@mui/material";
import {
  CheckCircle,
  Error,
  ArrowUpward,
  ArrowDownward,
  Print,
  ChevronLeft,
  ChevronRight,
  ViewColumn,
  MoreVert,
  Add,
  Remove
} from "@mui/icons-material";

const ExcelTable = ({ data }) => {
  const [rapportXValues, setRapportXValues] = useState({});
  const [savingStatus, setSavingStatus] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [compactMode, setCompactMode] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const restaurantId = localStorage.getItem("restaurantId");
  const isMobile = useMediaQuery('(max-width:600px)');

  // Safe data check
  const safeData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data;
  }, [data]);

  // Get unique categories from safe data
  const categories = useMemo(() => {
    if (!safeData.length) return { encaissement: [], decaissement: [] };
    
    const encaissementTypes = [...new Set(
      safeData
        .filter(r => r && r.category === 'ENCAISSEMENT' && r.type)
        .map(r => r.type)
    )];
    
    const decaissementTypes = [...new Set(
      safeData
        .filter(r => r && r.category === 'DECAISSEMENT' && r.type)
        .map(r => r.type)
    )];
    
    return {
      encaissement: encaissementTypes,
      decaissement: decaissementTypes
    };
  }, [safeData]);

  const totalTypes = categories.encaissement.length + categories.decaissement.length;

  // Dynamic sizing based on number of types
  const getColumnWidths = () => {
    if (totalTypes <= 4) return { date: 80, type: 85, total: 80, rapportX: 85 };
    if (totalTypes <= 6) return { date: 75, type: 75, total: 75, rapportX: 80 };
    if (totalTypes <= 8) return { date: 70, type: 70, total: 70, rapportX: 75 };
    if (totalTypes <= 10) return { date: 65, type: 65, total: 65, rapportX: 70 };
    return { date: 60, type: 60, total: 60, rapportX: 65 };
  };

  const columnWidths = getColumnWidths();
  
  const getFontSize = () => {
    if (totalTypes <= 4) return '0.7rem';
    if (totalTypes <= 6) return '0.65rem';
    if (totalTypes <= 8) return '0.6rem';
    return '0.55rem';
  };

  const getCellPadding = () => {
    if (totalTypes <= 4) return 0.75;
    if (totalTypes <= 6) return 0.6;
    if (totalTypes <= 8) return 0.5;
    return 0.4;
  };

  const fontSize = getFontSize();
  const cellPadding = getCellPadding();

  // Calcul du total en temps réel
  const totalRapportX = useMemo(() => {
    return Object.values(rapportXValues).reduce((sum, value) => sum + Number(value || 0), 0);
  }, [rapportXValues]);

  // Group data by month with safe date handling
  const { monthsData, initialPage } = useMemo(() => {
    if (!safeData.length) return { monthsData: [], initialPage: 0 };

    const groupedByMonth = safeData.reduce((acc, row) => {
      if (!row || !row.date) return acc;
      
      try {
        const date = new Date(row.date);
        // Check if date is valid
        if (isNaN(date.getTime())) return acc;
        
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!acc[monthKey]) {
          acc[monthKey] = {
            monthKey,
            dates: {},
            encaissement: {},
            decaissement: {},
            totals: { encaissement: 0, decaissement: 0, caisse: 0 }
          };
        }

        const category = row.category === 'ENCAISSEMENT' ? 'encaissement' : 'decaissement';
        const type = row.type || 'Autre';
        const value = parseFloat(row.value) || 0;
        
        // Daily values
        if (!acc[monthKey].dates[row.date]) {
          acc[monthKey].dates[row.date] = {
            date: row.date,
            encaissement: {},
            decaissement: {},
            totalCaisse: 0
          };
        }
        
        acc[monthKey].dates[row.date][category][type] = 
          (acc[monthKey].dates[row.date][category][type] || 0) + value;
        acc[monthKey].dates[row.date].totalCaisse += value;

        // Monthly totals
        acc[monthKey].totals[category] += value;
        acc[monthKey].totals.caisse += value;

        return acc;
      } catch (error) {
        console.error("Error processing row:", row, error);
        return acc;
      }
    }, {});

    const monthsDataArray = Object.values(groupedByMonth)
      .sort((a, b) => new Date(b.monthKey) - new Date(a.monthKey))
      .map(month => ({
        ...month,
        dates: Object.values(month.dates).sort((a, b) => new Date(b.date) - new Date(a.date))
      }));

    // Find current month index
    const currentDate = new Date();
    const currentMonthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    const initialPageIndex = monthsDataArray.findIndex(m => m.monthKey === currentMonthKey);

    return { monthsData: monthsDataArray, initialPage: initialPageIndex !== -1 ? initialPageIndex : 0 };
  }, [safeData]);

  useEffect(() => {
    if (monthsData.length > 0 && initialPage >= 0 && initialPage < monthsData.length) {
      setCurrentPage(initialPage);
    } else if (monthsData.length > 0) {
      setCurrentPage(0);
    }
  }, [initialPage, monthsData]);

  useEffect(() => {
    const fetchRapportX = async () => {
      if (!restaurantId) return;
      
      try {
        const allRapportX = await RapportX.getAllRapportX(restaurantId);
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
  
        // Filter items to only include those with dates in the current month and year
        const filteredRapportX = allRapportX.filter(item => {
          if (!item || !item.date) return false;
          const itemDate = new Date(item.date);
          return !isNaN(itemDate.getTime()) && 
                 itemDate.getMonth() === currentMonth &&
                 itemDate.getFullYear() === currentYear;
        });
  
        // Map the filtered items to an object with the date as key and the value as value
        const mappedValues = filteredRapportX.reduce((acc, item) => {
          acc[item.date] = item.value;
          return acc;
        }, {});
  
        setRapportXValues(mappedValues);
      } catch (error) {
        console.error("Error fetching RapportX:", error.message);
      }
    };
  
    fetchRapportX();
  }, [restaurantId]);

  const handleInputChange = async (date, value) => {
    if (!restaurantId) return;
    
    setRapportXValues((prev) => ({ ...prev, [date]: value }));
    setSavingStatus((prev) => ({ ...prev, [date]: 'saving' }));

    try {
      await RapportX.saveRapportX(restaurantId, date, Number(value));
      setSavingStatus((prev) => ({ ...prev, [date]: 'saved' }));
      setTimeout(() => {
        setSavingStatus((prev) => {
          const newStatus = { ...prev };
          delete newStatus[date];
          return newStatus;
        });
      }, 2000);
    } catch (error) {
      console.error("Error saving RapportX:", error.message);
      setSavingStatus((prev) => ({ ...prev, [date]: 'error' }));
      setTimeout(() => {
        setSavingStatus((prev) => {
          const newStatus = { ...prev };
          delete newStatus[date];
          return newStatus;
        });
      }, 3000);
    }
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
  };

  const getEcartColor = (value) => value >= 0 ? '#2e7d32' : '#d32f2f';
  const getEcartIcon = (value) => value >= 0 ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />;

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 0));
  };
  
  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, monthsData.length - 1));
  };
  
  const formatMonth = (monthKey) => {
    if (!monthKey) return '';
    const [year, month] = monthKey.split('-');
    return new Date(year, month - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleCompactMode = () => {
    setCompactMode(!compactMode);
    handleMenuClose();
  };

  if (!monthsData.length || !safeData.length) {
    return (
      <Paper elevation={0} sx={{ p: 2, textAlign: 'center', border: '1px solid #E0E0E0' }}>
        <Typography variant="body2" color="textSecondary">
          Aucune donnée financière disponible
        </Typography>
      </Paper>
    );
  }

  const currentMonth = monthsData[currentPage];
  
  // Safety check for currentMonth
  if (!currentMonth || !currentMonth.dates) {
    return (
      <Paper elevation={0} sx={{ p: 2, textAlign: 'center', border: '1px solid #E0E0E0' }}>
        <Typography variant="body2" color="textSecondary">
          Données du mois non disponibles
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ 
      border: '1px solid #E0E0E0', 
      borderRadius: 2,
      overflow: 'hidden'
    }}>
      {/* Header with controls */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: 1.5,
        borderBottom: '1px solid #E0E0E0',
        bgcolor: '#FAFAFA'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={handlePreviousPage} 
            disabled={currentPage === 0}
            size="small"
            sx={{ width: 28, height: 28 }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2E7D32' }}>
            {formatMonth(currentMonth.monthKey)}
          </Typography>
          <IconButton 
            onClick={handleNextPage} 
            disabled={currentPage === monthsData.length - 1}
            size="small"
            sx={{ width: 28, height: 28 }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Options d'affichage">
            <IconButton 
              onClick={handleMenuOpen} 
              size="small"
              sx={{ width: 28, height: 28 }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton onClick={() => window.print()} size="small" aria-label="Imprimer">
            <Print fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Display Options Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 180,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }
        }}
      >
        <MenuItem onClick={toggleCompactMode}>
          <ListItemIcon>
            {compactMode ? <Add fontSize="small" /> : <Remove fontSize="small" />}
          </ListItemIcon>
          <ListItemText>
            {compactMode ? 'Mode normal' : 'Mode compact'}
          </ListItemText>
          <Typography variant="caption" sx={{ color: '#9AA6B2', ml: 1 }}>
            {compactMode ? 'Activer' : 'Désactiver'}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem disabled>
          <ListItemIcon>
            <ViewColumn fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={`${totalTypes} colonnes`} />
          <Typography variant="caption" sx={{ color: '#2E7D32' }}>
            Auto-ajusté
          </Typography>
        </MenuItem>
      </Menu>

      {/* Info banner when many types */}
      {totalTypes > 8 && (
        <Box sx={{ 
          p: 0.75, 
          bgcolor: alpha('#FF9800', 0.1),
          borderBottom: '1px solid #E0E0E0',
          textAlign: 'center'
        }}>
          <Typography variant="caption" sx={{ color: '#ED6C02', fontSize: '0.55rem' }}>
            💡 {totalTypes} types de transactions - Tableau optimisé pour l'affichage compact
          </Typography>
        </Box>
      )}

      <TableContainer sx={{ maxHeight: '65vh', overflowX: 'auto' }}>
        <Table 
          size="small" 
          sx={{ 
            minWidth: columnWidths.date + 
              (categories.encaissement.length * columnWidths.type) + 
              (categories.decaissement.length * columnWidths.type) + 
              columnWidths.total + columnWidths.rapportX + columnWidths.total,
            '& .MuiTableCell-root': {
              py: cellPadding,
              px: compactMode ? 0.5 : 0.75,
              fontSize: fontSize,
              borderBottom: '1px solid #F0F0F0',
            }
          }}
          stickyHeader
        >
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  fontWeight: 600, 
                  width: columnWidths.date,
                  backgroundColor: '#F5F7FA',
                  borderRight: '1px solid #E0E0E0',
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  fontSize: fontSize
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  📅 Date
                </Box>
              </TableCell>
              
              {/* Encaissement Types */}
              {categories.encaissement.map(type => (
                <TableCell 
                  key={`enc-${type}`} 
                  sx={{ 
                    fontWeight: 600, 
                    bgcolor: '#E8F5E9', 
                    width: columnWidths.type,
                    p: cellPadding,
                    fontSize: fontSize
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: fontSize, fontWeight: 600 }}>
                      {type.length > (compactMode ? 8 : 10) ? `${type.substring(0, compactMode ? 6 : 8)}...` : type}
                    </Typography>
                  </Box>
                </TableCell>
              ))}
              
              {/* Decaissement Types */}
              {categories.decaissement.map(type => (
                <TableCell 
                  key={`dec-${type}`} 
                  sx={{ 
                    fontWeight: 600, 
                    bgcolor: '#FFEBEE', 
                    width: columnWidths.type,
                    p: cellPadding,
                    fontSize: fontSize
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: fontSize, fontWeight: 600 }}>
                      {type.length > (compactMode ? 8 : 10) ? `${type.substring(0, compactMode ? 6 : 8)}...` : type}
                    </Typography>
                  </Box>
                </TableCell>
              ))}
              
              <TableCell 
                sx={{ 
                  fontWeight: 600, 
                  bgcolor: '#E3F2FD', 
                  width: columnWidths.total,
                  p: cellPadding,
                  fontSize: fontSize
                }}
              >
                Total Quotidien
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 600, 
                  bgcolor: '#E3F2FD', 
                  width: columnWidths.rapportX,
                  p: cellPadding,
                  fontSize: fontSize
                }}
              >
                Rapport X
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 600, 
                  bgcolor: '#E3F2FD', 
                  width: columnWidths.total,
                  p: cellPadding,
                  fontSize: fontSize
                }}
              >
                Écart
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {currentMonth.dates && currentMonth.dates.slice(0, compactMode ? 6 : 8).map((dateRow) => {
              if (!dateRow || !dateRow.date) return null;
              
              const rapportX = rapportXValues[dateRow.date] || 0;
              const ecart = (dateRow.totalCaisse || 0) - rapportX;

              return (
                <TableRow key={dateRow.date} hover>
                  <TableCell 
                    sx={{ 
                      p: cellPadding,
                      position: 'sticky',
                      left: 0,
                      bgcolor: '#FFFFFF',
                      borderRight: '1px solid #E0E0E0',
                      zIndex: 1,
                      fontSize: fontSize
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: fontSize }}>
                      {new Date(dateRow.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                    </Typography>
                  </TableCell>
                  
                  {categories.encaissement.map(type => (
                    <TableCell 
                      key={`${dateRow.date}-enc-${type}`} 
                      sx={{ bgcolor: '#F1F8E9', p: cellPadding, fontSize: fontSize }}
                      align="right"
                    >
                      <Typography variant="caption" sx={{ fontSize: fontSize }}>
                        {formatNumber(dateRow.encaissement?.[type] || 0)}
                      </Typography>
                    </TableCell>
                  ))}
                  
                  {categories.decaissement.map(type => (
                    <TableCell 
                      key={`${dateRow.date}-dec-${type}`} 
                      sx={{ bgcolor: '#FFEBEE', p: cellPadding, fontSize: fontSize }}
                      align="right"
                    >
                      <Typography variant="caption" sx={{ fontSize: fontSize }}>
                        {formatNumber(dateRow.decaissement?.[type] || 0)}
                      </Typography>
                    </TableCell>
                  ))}
                  
                  <TableCell sx={{ bgcolor: '#E1F5FE', p: cellPadding, fontSize: fontSize }} align="right">
                    <Typography variant="caption" sx={{ fontWeight: 500, fontSize: fontSize }}>
                      {formatNumber(dateRow.totalCaisse || 0)}
                    </Typography>
                  </TableCell>
                  
                  <TableCell sx={{ bgcolor: '#E1F5FE', p: cellPadding }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        value={rapportX || ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/,/g, '');
                          if (/^\d*\.?\d*$/.test(value)) {
                            handleInputChange(dateRow.date, value);
                          }
                        }}
                        onBlur={(e) => {
                          const value = Number(e.target.value.replace(/,/g, ''));
                          if (!isNaN(value)) {
                            handleInputChange(dateRow.date, value);
                          }
                        }}
                        sx={{ 
                          width: compactMode ? '65px' : '70px',
                          '& .MuiInputBase-input': { 
                            fontSize: fontSize,
                            p: 0.5
                          }
                        }}
                        inputProps={{ 
                          style: { fontSize: fontSize },
                          type: 'text',
                          inputMode: 'decimal'
                        }}
                      />
                      {savingStatus[dateRow.date] === 'saving' && <CircularProgress size={12} />}
                      {savingStatus[dateRow.date] === 'saved' && <CheckCircle color="success" fontSize="small" sx={{ fontSize: 12 }} />}
                      {savingStatus[dateRow.date] === 'error' && <Error color="error" fontSize="small" sx={{ fontSize: 12 }} />}
                    </Box>
                  </TableCell>
                  
                  <TableCell sx={{ color: getEcartColor(ecart), p: cellPadding }} align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      {getEcartIcon(ecart)}
                      <Typography variant="caption" sx={{ fontSize: fontSize }}>
                        {formatNumber(Math.abs(ecart))}
                      </Typography>
                      {Math.abs(ecart) > 100 && (
                        <Tooltip title="Écart significatif">
                          <Error color="error" fontSize="small" sx={{ fontSize: 12 }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Monthly Summary Row */}
            <TableRow sx={{ bgcolor: '#E8EAF6', borderTop: '2px solid #E0E0E0' }}>
              <TableCell 
                colSpan={categories.encaissement.length + categories.decaissement.length + 2} 
                align="right"
                sx={{ p: cellPadding }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: fontSize }}>
                  Total Mensuel: {formatNumber(currentMonth.totals?.caisse || 0)}
                </Typography>
              </TableCell>
              <TableCell colSpan={2} align="right" sx={{ p: cellPadding }}>
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: fontSize }}>
                  Écart Mensuel: {formatNumber((currentMonth.totals?.caisse || 0) - totalRapportX)}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={monthsData.length}
        page={currentPage}
        onPageChange={(e, newPage) => setCurrentPage(newPage)}
        rowsPerPage={1}
        rowsPerPageOptions={[]}
        labelDisplayedRows={({ from, to, count }) => `${formatMonth(monthsData[currentPage]?.monthKey || '')}`}
        sx={{ 
          borderTop: '1px solid #E0E0E0',
          '& .MuiTablePagination-displayedRows': {
            fontSize: '0.7rem'
          }
        }}
      />
    </Paper>
  );
};

export default ExcelTable;